/**
 * Schedule-specific loading overlay shown while projects, groups, and scope
 * blocks are being prepared for the timeline.
 */
import { Spinner } from "../../../components/ui";
import { Check } from "../../../components/icons/svg/outline";

/**
 * Full-width overlay that fades over the schedule body during initial load.
 *
 * @param {Object} props
 * @param {boolean} props.visible Whether the overlay is visible or fading out.
 * @param {boolean} props.complete Whether loading has completed and the success icon should replace the spinner.
 * @param {number} [props.durationMs=550] Overall overlay fade duration; internal icon timing is derived from this.
 * @returns {JSX.Element}
 */
export default function ScheduleLoadingOverlay({
  visible,
  complete,
  durationMs = 550,
}) {
  const iconDurationMs = Math.round(durationMs * 0.55);

  return (
    <div
      className={`absolute inset-0 z-[20] bg-white transition-opacity duration-500 ${
        visible ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
      style={{ transitionDuration: `${durationMs}ms` }}
    >
      <div className="flex h-[260px] items-center justify-center">
        <div className="w-[320px] rounded-lg border border-gray-200 bg-gray-100 px-4 py-3 text-center shadow-sm">
          <div className="relative flex h-8 items-center justify-center">
            <div
              className={`transition-opacity duration-300 ${
                complete ? "opacity-0" : "opacity-100"
              }`}
              style={{ transitionDuration: `${iconDurationMs}ms` }}
            >
              <Spinner
                className="text-primary"
                size="h-8 w-8"
                variant="oval"
                color="currentColor"
                secondaryColor="#D1D5DB"
                spinDurationSeconds={2}
              />
            </div>
            <Check
              className={`absolute h-8 w-8 text-green-900 transition-opacity duration-300 ${
                complete ? "opacity-100" : "opacity-0"
              }`}
              style={{ transitionDuration: `${iconDurationMs}ms` }}
            />
          </div>
          <p className="mt-2 text-sm font-medium text-gray-800">
            Loading Schedule Data
          </p>
          <p className="mt-1 text-xs text-gray-500">
            Projects, groups, and scope blocks are being prepared.
          </p>
        </div>
      </div>
    </div>
  );
}
